import React from 'react';
import { Pressable, TextInput as RNTextInput } from 'react-native';
import { Text } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { AlertCircle, Clock } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import {
  getCardBackgroundColor,
  getCardBorderColor,
  getInputBackgroundColor,
  getInputBorderColor,
  getButtonBackgroundColor,
  ALERT_MINIMUM_WITHDRAWAL_BG,
  ALERT_MINIMUM_WITHDRAWAL_COLOR,
  CARD_BORDER_RADIUS,
  CARD_PADDING,
  BORDER_RADIUS_MEDIUM,
  BUTTON_BORDER_RADIUS,
  INPUT_PADDING,
  INPUT_FONT_SIZE,
  FONT_SIZE_LG,
  FONT_SIZE_SM,
  FONT_SIZE_XS,
  FONT_SIZE_BASE,
  ICON_SIZE_SMALL,
  BORDER_WIDTH_THIN,
} from '../constants';

export type WithdrawTabProps = {
  isDark: boolean;
  textColor: string;
  subtextColor: string;
  withdrawAmount: string;
  bankAccount: string;
  bankName: string;
  canWithdraw: boolean;
  onWithdrawAmountChange: (text: string) => void;
  onBankAccountChange: (text: string) => void;
  onBankNameChange: (text: string) => void;
  onWithdraw: () => void;
};

const WithdrawTab: React.FC<WithdrawTabProps> = React.memo(({
  isDark,
  textColor,
  subtextColor,
  withdrawAmount,
  bankAccount,
  bankName,
  canWithdraw,
  onWithdrawAmountChange,
  onBankAccountChange,
  onBankNameChange,
  onWithdraw,
}) => {
  return (
    <YStack space="$4">
      {/* Info about minimum withdrawal */}
      <XStack
        backgroundColor={ALERT_MINIMUM_WITHDRAWAL_BG}
        padding="$3"
        borderRadius={BORDER_RADIUS_MEDIUM}
        alignItems="center"
        gap="$2"
      >
        <AlertCircle size={ICON_SIZE_SMALL} color={ALERT_MINIMUM_WITHDRAWAL_COLOR} />
        <Text fontSize={FONT_SIZE_SM} fontWeight="700" color={ALERT_MINIMUM_WITHDRAWAL_COLOR}>
          Minimalna kwota wypłaty: 50 zł
        </Text>
      </XStack>

      {/* Withdrawal Form */}
      <YStack
        backgroundColor={getCardBackgroundColor(isDark)}
        borderRadius={CARD_BORDER_RADIUS}
        padding={CARD_PADDING}
        borderWidth={BORDER_WIDTH_THIN}
        borderColor={getCardBorderColor(isDark)}
        space="$3"
      >
        <Text fontSize={FONT_SIZE_LG} fontWeight="700" color={textColor}>
          Kwota wypłaty:
        </Text>
        
        <RNTextInput
          value={withdrawAmount}
          onChangeText={onWithdrawAmountChange}
          placeholder="Wpisz kwotę..."
          placeholderTextColor={subtextColor}
          keyboardType="decimal-pad"
          style={{
            backgroundColor: getInputBackgroundColor(isDark),
            borderRadius: BORDER_RADIUS_MEDIUM,
            padding: INPUT_PADDING,
            fontSize: INPUT_FONT_SIZE,
            fontWeight: '500',
            color: textColor,
            borderWidth: BORDER_WIDTH_THIN,
            borderColor: getInputBorderColor(isDark),
          }}
        />

        <Text fontSize={FONT_SIZE_LG} fontWeight="700" color={textColor} marginTop="$2">
          Numer konta bankowego
        </Text>
        
        <RNTextInput
          value={bankAccount}
          onChangeText={onBankAccountChange}
          placeholder="PL00 0000 0000 0000 0000 0000 0000"
          placeholderTextColor={subtextColor}
          keyboardType="default"
          style={{
            backgroundColor: getInputBackgroundColor(isDark),
            borderRadius: BORDER_RADIUS_MEDIUM,
            padding: INPUT_PADDING,
            fontSize: INPUT_FONT_SIZE,
            fontWeight: '500',
            color: textColor,
            borderWidth: BORDER_WIDTH_THIN,
            borderColor: getInputBorderColor(isDark),
          }}
        />

        <Text fontSize={FONT_SIZE_LG} fontWeight="700" color={textColor} marginTop="$2">
          Nazwa banku
        </Text>
        
        <RNTextInput
          value={bankName}
          onChangeText={onBankNameChange}
          placeholder="Nazwa banku"
          placeholderTextColor={subtextColor}
          style={{
            backgroundColor: getInputBackgroundColor(isDark),
            borderRadius: BORDER_RADIUS_MEDIUM,
            padding: INPUT_PADDING,
            fontSize: INPUT_FONT_SIZE,
            fontWeight: '500',
            color: textColor,
            borderWidth: BORDER_WIDTH_THIN,
            borderColor: getInputBorderColor(isDark),
          }}
        />

        <YStack
          backgroundColor={getInputBackgroundColor(isDark)}
          padding="$3"
          borderRadius={BORDER_RADIUS_MEDIUM}
          marginTop="$2"
        >
          <XStack alignItems="center" gap="$2">
            <Clock size={ICON_SIZE_SMALL} color={subtextColor} />
            <Text fontSize={FONT_SIZE_XS} color={subtextColor}>
              Wypłaty są przetwarzane w ciągu 1-3 dni roboczych
            </Text>
          </XStack>
        </YStack>

        {/* Withdraw button */}
        <Pressable
          onPress={onWithdraw}
          disabled={!canWithdraw}
        >
          <YStack
            backgroundColor={getButtonBackgroundColor(canWithdraw)}
            padding="$4"
            borderRadius={BUTTON_BORDER_RADIUS}
            alignItems="center"
            marginTop="$3"
          >
            <Text fontSize={FONT_SIZE_BASE} fontWeight="700" color={COLORS.WHITE}>
              Zlecenie wypłaty
            </Text>
          </YStack>
        </Pressable>
      </YStack>
    </YStack>
  );
});

WithdrawTab.displayName = 'WithdrawTab';

export default WithdrawTab;

